export type Nullable<T> = T | null;

export type Optional<T> = T | undefined;

export type Nullish<T> = T | null | undefined;

export type DeepPartial<T> = T extends Date
  ? T
  : T extends Array<any>
    ? T
    : T extends object
      ? { [P in keyof T]?: DeepPartial<T[P]> }
      : T;

export type SloppyPartial<T> = {
  [K in keyof T]?: Nullish<T[K]>;
};

export type UndefinedToVoid<T> = undefined extends T ? void : T;

export type IsExact<T, U> = [T] extends [U]
  ? [U] extends [T]
    ? true
    : false
  : false;

export type NoExtraProperties<T, U extends T = T> = U &
  Impossible<Exclude<keyof U, keyof T>>;

type Impossible<K extends keyof any> = {
  [P in K]: never;
};

export type Subset<T, U extends T> = Extract<T, U>;

export type NonEmptyArray<T> = [T, ...T[]];

/**
 * Like Pick, but distributes over union types, preserving discriminated unions.
 * Useful when you want to pick properties from a discriminated union while
 * maintaining the relationship between the discriminant and other properties.
 */
export type DistributivePick<T, K extends keyof T> = T extends any
  ? Pick<T, K>
  : never;

/**
 * Like Omit, but distributes over union types, preserving discriminated unions.
 */
export type DistributiveOmit<T, K extends keyof T> = T extends any
  ? Omit<T, K>
  : never;

/**
 * Makes specified properties K of type T optional while keeping all other properties as-is.
 * The inverse of WithRequired.
 */
export type WithOptional<T, K extends keyof T> = Omit<T, K> &
  Partial<Pick<T, K>>;

/**
 * Makes specified properties K of type T required while keeping all other properties as-is.
 * The inverse of WithOptional.
 */
export type WithRequired<T, K extends keyof T> = Required<Pick<T, K>> &
  Omit<T, K>;

/**
 * A type for values of unknown type, which allows speculative
 * access to properties using the `?.` operator, returning all
 * properties as `SafeAny`.
 */
export type SafeAny =
  | null
  | undefined
  | ({ [k: string | number | symbol]: SafeAny } & (
      | string
      | number
      | boolean
      | symbol
      | bigint
      | object
    ));

/**
 * Convert the type of an object to a `SafeAny` to allow
 * speculative access to its properties without TypeScript
 * complaining about unknown properties.
 */
export function safeAny(value: unknown): SafeAny {
  return value as SafeAny;
}

/**
 * Identity function that enforces at compile time that the array contains
 * every member of string union T. If a value is missing, TS reports a type
 * error showing which values are absent.
 *
 * Usage: `exhaustiveArray<MyStringUnion>()(['A', 'B', 'C'])`
 */
export function exhaustiveArray<T extends string>() {
  return <const A extends readonly T[]>(
    arr: A &
      ([T] extends [A[number]] ? unknown : `Missing: ${Exclude<T, A[number]>}`)
  ) => arr;
}
